import { deviceApi } from "#/api/deviceApi";
import { rfidTagApi } from "#/api/rfidTagApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import SelectableTable from "#/components/tables/SelectableTable";
import type { TableSortProps } from "#/components/tables/BaseTable";
import type { ColumnKey } from "#/config/tableColumnsConfig";
import { useTableColumns, type ColumnRenderer } from "#/hooks/useTableColumns";
import type { RfidTag } from "#/types/rfidTag";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import { exportToCSV } from "#/utils/CsvUtils";
import { getHardwareLabel } from "#/utils/deviceHardware";
import { logger } from "#/utils/logger";
import {
  Badge,
  Button,
  SegmentedControl,
  Select,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBox,
  IconCar,
  IconDeviceMobile,
  IconDownload,
  IconLink,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import BulkCreateItemsModal from "./components/BulkCreateItemsModal";
import BulkDeleteModal from "./components/BulkDeleteModal";
import DeleteRfidTagModal from "./components/DeleteRfidTagModal";
import EditRfidTagModal from "./components/EditRfidTagModal";
import LinkRfidTagToItemModal from "./components/LinkRfidTagToItemModal";
import RfidLinkCard from "./components/RfidLinkCard";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

type RfidTab = "linked" | "not_linked";

/** A loose tag has nothing to be linked to, so that column is not part of the Not Linked view. */
const NOT_LINKED_EXCLUDED_COLUMNS: readonly ColumnKey<"rfidTags">[] = ["linked_to"];
/** SelectableTable prepends its checkbox column. */
const LEADING_COLUMNS = 1;

const isRfidTab = (value: string | null): value is RfidTab =>
  value === "linked" || value === "not_linked";

/** CSV columns, shared by the current-page and the export-all flows. */
const EXPORT_HEADERS = [
  "rfid",
  "type",
  "status",
  "linked_to",
  "linked_item_id",
  "linked_vehicle_id",
  "last_scan",
  "createdAt",
  "updatedAt",
] as const;

const EXPORT_HEADER_LABELS: Record<(typeof EXPORT_HEADERS)[number], string> = {
  rfid: "RFID",
  type: "Type",
  status: "Status",
  linked_to: "Linked To",
  linked_item_id: "Linked Item ID",
  linked_vehicle_id: "Linked Vehicle ID",
  last_scan: "Last Scan",
  createdAt: "Created At",
  updatedAt: "Last Updated",
};

const RfidTagsPage = () => {
  const { t } = useTranslation();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  // The tab lives in the URL so a reload (or a shared link) lands on the same list.
  const tabFromUrl = searchParams.get("tab");
  const activeTab: RfidTab = isRfidTab(tabFromUrl) ? tabFromUrl : "linked";
  const setActiveTab = (tab: RfidTab) => setSearchParams({ tab }, { replace: true });
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [search, setSearch] = useState<string>("");
  const [scannerFilter, setScannerFilter] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [selectedRfidIds, setSelectedRfidIds] = useState<number[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    bulkDeleteModalOpened,
    { open: openBulkDeleteModal, close: closeBulkDeleteModal },
  ] = useDisclosure(false);
  const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] =
    useDisclosure(false);
  const [
    bulkCreateModalOpened,
    { open: openBulkCreateModal, close: closeBulkCreateModal },
  ] = useDisclosure(false);
  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);
  const [
    linkItemModalOpened,
    { open: openLinkItemModal, close: closeLinkItemModal },
  ] = useDisclosure(false);
  const [rfidTagToDelete, setRfidTagToDelete] = useState<RfidTag>();
  const [rfidTagToLink, setRfidTagToLink] = useState<RfidTag>();
  const [rfidTagToLinkItem, setRfidTagToLinkItem] = useState<RfidTag>();

  // Row selection is by index into the current page, so any change of what
  // the page shows must drop it: stale indices would point at other rows.
  const clearSelection = () => {
    setSelectedRfidIds([]);
    setSelectedIndices([]);
  };
  useEffect(() => {
    clearSelection();
  }, [activeTab, page, limit, search, scannerFilter, sortBy, sortDirection]);

  const { data: { devices = [] } = {} } = useQuery({
    queryKey: ["rfidTags-scanner-filter-devices"],
    queryFn: async () => {
      const response = await deviceApi.getAll({ page: 1, limit: 10000 });
      return { devices: response.data.data };
    },
    staleTime: 1000 * 60 * 5,
  });

  const scannerOptions = devices
    .filter((device) => device.id != null && device.name)
    .map((device) => ({
      value: String(device.id),
      label: device.name as string,
    }));

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getRfidTags,
  } = useQuery({
    queryKey: ["rfidTags", sortBy, page, limit, sortDirection, search, activeTab, scannerFilter],
    queryFn: async () => {
      try {
        const response = await rfidTagApi.getAll({
          limit: limit,
          page: page,
          ...(search ? { search: search } : {}),
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
          ...(scannerFilter ? { last_read_by_device_id: Number(scannerFilter) } : {}),
          linked: activeTab === "linked",
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch {
        throw new Error("No RFID tags returned");
      }
    },
    staleTime: 1000 * 60,
    retry: false,
  });


  const getTypeColor = (type: string) => {
    switch (type) {
      case "hard":
        return "blue";
      case "label":
        return "orange";
      default:
        return "gray";
    }
  };

  const renderLinkedTo = (rfidTag: RfidTag) => {
    if (rfidTag.item) {
      return (
        <RfidLinkCard
          variant="item"
          label={t("tickets.form.item")}
          path={`/inventory/items/${rfidTag.item.id}`}
          icon={<IconBox size={14} />}
        />
      );
    }
    if (rfidTag.vehicle) {
      return (
        <RfidLinkCard
          variant="vehicle"
          label={t("vehicles.title")}
          path={`/fleet/vehicles/${rfidTag.vehicle.id}`}
          icon={<IconCar size={14} />}
        />
      );
    }
    return "--";
  };

  const renderLastScan = (rfidTag: RfidTag) => {
    if (!rfidTag.last_scan) {
      return "--";
    }
    return (
      <span>
        {formatDate(rfidTag.last_scan, client)}
        <br />
        {formatTime(rfidTag.last_scan, client)}
      </span>
    );
  };

  const renderScanner = (rfidTag: RfidTag) => {
    if (!rfidTag.lastReadByDevice) {
      return "--";
    }

    return (
      <RfidLinkCard
        variant="scanner"
        label={rfidTag.lastReadByDevice.name}
        path={`/devices/${rfidTag.lastReadByDevice.id}`}
        icon={<IconDeviceMobile size={14} />}
        deviceType={getHardwareLabel(rfidTag.lastReadByDevice.hardware_type, "")}
      />
    );
  };

  const renderDateTime = (value: string | null | undefined): ReactNode =>
    value ? (
      <span>
        {formatDate(value, client)}
        <br />
        {formatTime(value, client)}
      </span>
    ) : (
      "--"
    );

  // One renderer per admin-configurable column (compile-time complete); both
  // tabs share it, the Not Linked tab just leaves "linked to" out.
  const renderers: Record<ColumnKey<"rfidTags">, ColumnRenderer<RfidTag>> = {
    rfid: { cell: (tag) => tag.rfid ?? "--", cellClassName: "font-mono", sortKey: "rfid" },
    type: {
      cell: (tag) => (
        <Badge color={getTypeColor(tag.type)} variant="light" className="capitalize">
          {tag.type}
        </Badge>
      ),
      cellClassName: "w-[125px]",
      sortKey: "type",
    },
    linked_to: { cell: renderLinkedTo, cellClassName: "w-[200px] min-h-[60px]" },
    last_scanned_by: {
      cell: renderScanner,
      cellClassName: "w-[300px] min-h-[60px]",
      },
    last_scan: { cell: renderLastScan, cellClassName: "w-[150px]", sortKey: "last_scan" },
    createdAt: {
      cell: (tag) => renderDateTime(tag.createdAt),
      cellClassName: "w-[150px]",
      sortKey: "createdAt",
    },
    updatedAt: {
      cell: (tag) => renderDateTime(tag.updatedAt),
      cellClassName: "w-[150px]",
      sortKey: "updatedAt",
    },
  };

  const linkedColumns = useTableColumns("rfidTags", renderers, {
    leadingColumns: LEADING_COLUMNS,
  });
  const notLinkedColumns = useTableColumns("rfidTags", renderers, {
    leadingColumns: LEADING_COLUMNS,
    exclude: NOT_LINKED_EXCLUDED_COLUMNS,
  });
  const columns = activeTab === "linked" ? linkedColumns : notLinkedColumns;

  const handleSortChange = (e: TableSortProps) => {
    const key = columns.sortKeyAt(e.index);
    if (!key) return;
    setPage(1);
    setSortDirection(e.direction);
    setSortBy(key);
  };

  const handleSelectionChange = (newIndices: number[]) => {
    const selectedTags = newIndices.map((index) => items[index]);
    setSelectedRfidIds(selectedTags.map((tag) => tag.id));
    setSelectedIndices(newIndices);
  };

  const defaultSortIndex = columns.indexOf("updatedAt");

  const renderDataCells = (tag: RfidTag) =>
    columns.visible.map((column) => (
      <Table.Td key={column.key} className={column.cellClassName}>
        {column.cell(tag)}
      </Table.Td>
    ));

  const prepareExportData = (itemsToExport: typeof items) => {
    return itemsToExport.map((item) => ({
      rfid: item.rfid || "",
      type: item.type || "",
      // Decided by the real link, never by the stored status field.
      status: item.item || item.vehicle ? "linked" : "not_linked",
      linked_to: item.item ? "Item" : item.vehicle ? "Vehicle" : "Not Linked",
      linked_item_id: item.item?.id || "",
      linked_vehicle_id: item.vehicle?.id || "",
      last_scan: formatDateTimeForExport(item.last_scan),
      createdAt: formatDateTimeForExport(item.createdAt),
      updatedAt: formatDateTimeForExport(item.updatedAt),
    }));
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const filename = `rfid-tags-${activeTab}-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, [...EXPORT_HEADERS], filename, EXPORT_HEADER_LABELS, t("common.utcNote"));
  };

  const handleExportAll = async () => {
    try {
      const response = await rfidTagApi.getAll({
        limit: total,
        page: 1,
        ...(search ? { search: search } : {}),
        ...(sortBy ? { sortBy: sortBy } : {}),
        ...(sortDirection ? { order: sortDirection } : {}),
        ...(scannerFilter ? { last_read_by_device_id: Number(scannerFilter) } : {}),
        linked: activeTab === "linked",
      });

      const allItems = response.data.data;
      const exportData = prepareExportData(allItems);
      const filename = `rfid-tags-${activeTab}-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, [...EXPORT_HEADERS], filename, EXPORT_HEADER_LABELS, t("common.utcNote"));
    } catch (error) {
      logger.error("Failed to export all RFID tags:", error);
    }
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4 relative">
      <div className="flex  flex-col gap-4 bg-white z-10">
        <div className="flex items-center gap-2">
          <Title order={2}>{t("rfidTags.title")}</Title>
          <SegmentedControl
            data={[
              { label: t("rfidTags.linked"), value: "linked" },
              { label: t("rfidTags.notLinked"), value: "not_linked" },
            ]}
            value={activeTab}
            onChange={(value) => {
              if (!isRfidTab(value)) return;
              setActiveTab(value);
              setPage(1);
            }}
            size="xs"
          />
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            className="ml-auto"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
          >
            {t("common.exportCsv")}
          </Button>
        </div>
        <div className="w-full flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <TextInput
              className="flex-1 max-w-96"
              placeholder={t("rfidTags.searchPlaceholder")}
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
            />
            <Select
              className="w-64"
              placeholder={t("rfidTags.filterByScannerPlaceholder")}
              leftSection={<IconDeviceMobile size={16} />}
              data={scannerOptions}
              value={scannerFilter}
              onChange={(value) => {
                setScannerFilter(value);
                setPage(1);
              }}
              clearable
              searchable
              nothingFoundMessage={t("common.noData")}
            />
          </div>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {activeTab === "linked" && (
          <div className="flex-1 overflow-auto">
            <SelectableTable
              headers={[...columns.headers, t("common.actions")]}
              loading={isPending}
              selectedIndices={selectedIndices}
              onSelectionChange={handleSelectionChange}
              allowSorting={true}
              sortableColumns={columns.sortableColumns}
              onSortChange={handleSortChange}
              defaultSort={
                defaultSortIndex !== undefined
                  ? { direction: "DESC", index: defaultSortIndex }
                  : undefined
              }
              rows={items.map((item) => (
                <Table.Tr key={item.id}>
                  {renderDataCells(item)}
                  <Table.Td className="min-w-[100px] w-[100px]">
                    <div className="w-full flex items-center justify-center gap-2">
                      <PermissionTooltip disabled={!canWrite()}>
                        <ActionButton
                          icon={IconPencil}
                          variant="outline"
                          onClick={() => {
                            setRfidTagToLink(item);
                            openLinkModal();
                          }}
                          disabled={!canWrite()}
                        />
                      </PermissionTooltip>
                      <PermissionTooltip disabled={!canDelete()}>
                        <ActionButton
                          icon={IconTrash}
                          variant="outline"
                          color="red"
                          onClick={() => {
                            setRfidTagToDelete(item);
                            openDeleteModal();
                          }}
                          disabled={!canDelete()}
                        />
                      </PermissionTooltip>
                    </div>
                  </Table.Td>
                </Table.Tr>
              ))}
            />
          </div>
        )}

        {activeTab === "not_linked" && (
          <div className="flex-1 overflow-auto">
            <SelectableTable
              headers={[...columns.headers, t("common.actions")]}
              loading={isPending}
              selectedIndices={selectedIndices}
              onSelectionChange={handleSelectionChange}
              allowSorting={true}
              sortableColumns={columns.sortableColumns}
              onSortChange={handleSortChange}
              defaultSort={
                defaultSortIndex !== undefined
                  ? { direction: "DESC", index: defaultSortIndex }
                  : undefined
              }
              rows={items.map((item) => (
                <Table.Tr key={item.id}>
                  {renderDataCells(item)}
                  <Table.Td className="min-w-[140px] w-[140px]">
                    <div className="w-full flex items-center justify-center gap-2">
                      <PermissionTooltip disabled={!canWrite()}>
                        <ActionButton
                          icon={IconPencil}
                          variant="outline"
                          onClick={() => {
                            setRfidTagToLink(item);
                            openLinkModal();
                          }}
                          disabled={!canWrite()}
                        />
                      </PermissionTooltip>
                      <PermissionTooltip disabled={!canWrite()}>
                        <ActionButton
                          icon={IconLink}
                          variant="outline"
                          aria-label={t("rfidTags.linkItem.action")}
                          title={t("rfidTags.linkItem.action")}
                          onClick={() => {
                            setRfidTagToLinkItem(item);
                            openLinkItemModal();
                          }}
                          disabled={!canWrite()}
                        />
                      </PermissionTooltip>
                      <PermissionTooltip disabled={!canDelete()}>
                        <ActionButton
                          icon={IconTrash}
                          variant="outline"
                          color="red"
                          onClick={() => {
                            setRfidTagToDelete(item);
                            openDeleteModal();
                          }}
                          disabled={!canDelete()}
                        />
                      </PermissionTooltip>
                    </div>
                  </Table.Td>
                </Table.Tr>
              ))}
            />
          </div>
        )}
      </div>

      <DeleteRfidTagModal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        rfidTag={rfidTagToDelete}
        onSuccess={() => {
          setRfidTagToDelete(undefined);
          void getRfidTags();
        }}
      />

      <BulkDeleteModal
        opened={bulkDeleteModalOpened}
        onClose={closeBulkDeleteModal}
        selectedTags={items.filter((item) => selectedRfidIds.includes(item.id))}
        onSuccess={() => {
          clearSelection();
          void getRfidTags();
        }}
      />

      <EditRfidTagModal
        opened={linkModalOpened}
        onClose={closeLinkModal}
        rfidTag={rfidTagToLink}
        onSuccess={() => {
          setRfidTagToLink(undefined);
          void getRfidTags();
        }}
      />

      <BulkCreateItemsModal
        opened={bulkCreateModalOpened}
        onClose={closeBulkCreateModal}
        selectedTags={items.filter((item) => selectedRfidIds.includes(item.id))}
        onSuccess={() => {
          clearSelection();
          void getRfidTags();
        }}
      />

      <LinkRfidTagToItemModal
        opened={linkItemModalOpened}
        onClose={closeLinkItemModal}
        rfidTag={rfidTagToLinkItem}
        onSuccess={() => {
          setRfidTagToLinkItem(undefined);
          clearSelection();
          void getRfidTags();
        }}
      />

      {selectedRfidIds.length > 0 && (
        <div className=" absolute bottom-0 left-0 inset-x-0 z-50 flex items-center justify-between w-full bg-black/10 border-t border-black/50 backdrop-blur-sm p-4 ">
          <div className="flex items-center gap-2">
            <Text size="md" fw={600} c="dark">
              {selectedRfidIds.length} {t("common.selected")}
            </Text>
          </div>
          <div className="flex items-center gap-3">
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                leftSection={<IconTrash size={16} />}
                color="red"
                variant="filled"
                size="md"
                onClick={() => {
                  openBulkDeleteModal();
                }}
                disabled={!canDelete()}
              >
                {t("rfidTags.deleteSelected")}
              </Button>
            </PermissionTooltip>
            {activeTab === "not_linked" && (
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  leftSection={<IconPlus size={16} />}
                  color="black"
                  variant="filled"
                  size="md"
                  onClick={() => {
                    openBulkCreateModal();
                  }}
                  disabled={!canWrite()}
                >
                  {t("rfidTags.createItems")}
                </Button>
              </PermissionTooltip>
            )}
          </div>
        </div>
      )}

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default RfidTagsPage;
